import { Injectable } from '@nestjs/common';
import {
  Keypair,
  PublicKey,
  Transaction,
  VersionedTransaction,
} from '@solana/web3.js';
import { KeyManagementServiceClient } from '@google-cloud/kms';
import * as asn1 from 'asn1js';
import { bs58 } from '@coral-xyz/anchor/dist/cjs/utils/bytes';
import { toHex } from 'viem';
import { Wallet } from '@coral-xyz/anchor/dist/cjs/index.js';

export const isVersionedTransaction = (
  tx: Transaction | VersionedTransaction,
): tx is VersionedTransaction => {
  return 'version' in tx;
};

@Injectable()
export class KSMProvider {
  private cachedPubKey: PublicKey | undefined;

  constructor(
    private readonly kmsClient: KeyManagementServiceClient | null,
    private readonly hsmKeyVersion: string | null,
    private readonly oraclePrivateKey?: string,
  ) {}

  async signTransaction<T extends Transaction | VersionedTransaction>(
    tx: T,
  ): Promise<VersionedTransaction> {
    const transaction = isVersionedTransaction(tx)
      ? tx
      : new VersionedTransaction(tx.compileMessage());

    if (this.kmsClient) {
      await this._signTransactionKms(transaction);
    } else {
      await this._signTransactionOracle(transaction);
    }

    return transaction;
  }

  async signAllTransactions<T extends Transaction | VersionedTransaction>(
    txs: T[],
  ): Promise<T[]> {
    throw new Error('Not implemented exception');
  }

  get publicKey(): PublicKey {
    return this.cachedPubKey!;
  }

  async getPubKey() {
    if (this.cachedPubKey) return this.cachedPubKey;

    if (this.oraclePrivateKey) {
      const keypair = this.getOracleKeypair(this.oraclePrivateKey);
      this.cachedPubKey = keypair.publicKey;
      return this.cachedPubKey;
    }

    const [pk] = await this.kmsClient!.getPublicKey({
      name: this.hsmKeyVersion,
    });
    if (!pk.pem) {
      throw new Error('PublicKey pem is not defined');
    }
    const derEncodedPk = this.pemToDer(pk.pem);
    const hexKey = this.publicKeyFromDer(derEncodedPk);

    const pubKey = new PublicKey(
      bs58.encode(Uint8Array.from(Buffer.from(hexKey.slice(2), 'hex'))),
    );

    this.cachedPubKey = pubKey;

    return pubKey;
  }

  private publicKeyFromDer(bytes: Uint8Array): string {
    const { result } = asn1.fromBER(bytes);
    const values = (result as asn1.Sequence).valueBlock.value;
    if (values.length < 2) {
      throw new Error('Cannot get public key from ASN.1: invalid sequence');
    }
    const value = values[1] as asn1.BitString;

    return toHex(value.valueBlock.valueHexView);
  }

  private pemToDer(pem: string): Uint8Array {
    const base64 = pem.split('\n').slice(1, -2).join('').trim();
    return Buffer.from(base64, 'base64');
  }

  private async _signTransactionKms(tx: VersionedTransaction) {
    const messageData = tx.message.serialize();
    const signerPubkeys = tx.message.staticAccountKeys.slice(
      0,
      tx.message.header.numRequiredSignatures,
    );
    // for (const signer of signers) {
    const signerIndex = signerPubkeys.findIndex((pubkey) =>
      pubkey.equals(this.publicKey),
    );

    // make sure that the delay not affect tx execution
    const [signResponse] = await this.kmsClient!.asymmetricSign({
      name: this.hsmKeyVersion,
      data: messageData,
    })
      .catch(console.error)
      .then((v) => v || []);

    tx.signatures[signerIndex] = signResponse.signature as Uint8Array;
  }

  private async _signTransactionOracle(tx: VersionedTransaction) {
    const wallet = new Wallet(this.getOracleKeypair(this.oraclePrivateKey!));
    wallet.signTransaction(tx);
  }

  private getOracleKeypair(oraclePrivateKey: string) {
    const pk = bs58.decode(oraclePrivateKey);
    const keypair = Keypair.fromSecretKey(pk);
    return keypair;
  }
}
